import React, { useEffect, useState } from 'react';
import { apiGet, apiPost } from '../api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, Send, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../lib/apiError';

const STATUS_BADGE = {
  pending: { label: 'Pending', cls: 'bg-amber-500 text-white' },
  submitted_to_comreg: { label: 'Submitted to ComReg', cls: 'bg-sky-600 text-white' },
  active: { label: 'Active', cls: 'bg-emerald-500 text-white' },
  rejected: { label: 'Rejected', cls: 'bg-red-600 text-white' },
};

const ORG_TYPE_OPTIONS = [
  { value: 'company', label: 'Company' },
  { value: 'health_professional', label: 'Health Professional' },
  { value: 'partnership_sole_trader', label: 'Partnership / Sole Trader' },
];

const EMPTY_FORM = {
  requested_sender_id: '',
  organisation_type: 'company',
  organisation_phone: '',
  organisation_email: '',
  tax_registration_number: '',
  cro_number: '',
  registered_address: '',
  registered_city: '',
  registered_county: '',
  registered_eircode: '',
  business_address: '',
  business_city: '',
  business_county: '',
  business_eircode: '',
  contact_first_name: '',
  contact_last_name: '',
  contact_phone: '',
  contact_email: '',
  fee_acknowledged: false,
};

export const CustomSenderIdSection = () => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [request, setRequest] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [copyAddress, setCopyAddress] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await apiGet('/api/studio/sender-id-requests/me');
      setRequest(data);
    } catch (e) {
      toast.error(formatApiError(e, 'Could not load sender-ID status'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // When "copy registered → business" is ticked, mirror values live.
  useEffect(() => {
    if (!copyAddress) return;
    setForm((p) => ({
      ...p,
      business_address: p.registered_address,
      business_city: p.registered_city,
      business_county: p.registered_county,
      business_eircode: p.registered_eircode,
    }));
  }, [
    copyAddress,
    form.registered_address,
    form.registered_city,
    form.registered_county,
    form.registered_eircode,
  ]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.fee_acknowledged) {
      toast.error('You must acknowledge the €49/year recurring charge to submit');
      return;
    }
    setSubmitting(true);
    try {
      const data = await apiPost('/api/studio/sender-id-requests', { body: form });
      setRequest(data);
      toast.success('Sender ID request submitted. We\'ll review it shortly.');
      setForm(EMPTY_FORM);
      setCopyAddress(false);
    } catch (err) {
      toast.error(formatApiError(err, 'Could not submit sender-ID request'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  // ───────────────── Status view (existing OPEN request) ─────────────────
  // A 'rejected' request still shows its status, but we ALSO render the
  // submission form below so the studio can re-apply without contacting us.
  const showForm = !request || request.status === 'rejected';

  return (
    <div className="space-y-6">
      {request && (
        <StatusCard request={request} />
      )}
      {showForm && (
        <FormCard
          form={form}
          setForm={setForm}
          copyAddress={copyAddress}
          setCopyAddress={setCopyAddress}
          submitting={submitting}
          onSubmit={submit}
          isResubmit={request?.status === 'rejected'}
        />
      )}
    </div>
  );
};

// ───────────────────────── Status card ─────────────────────────
const StatusCard = ({ request }) => {
  const meta = STATUS_BADGE[request.status] || STATUS_BADGE.pending;
  return (
    <Card data-testid="sender-id-status-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              Custom Sender ID
            </CardTitle>
            <CardDescription>
              Status of your personalised SMS sender ID request.
            </CardDescription>
          </div>
          <Badge className={meta.cls} data-testid="sender-id-status-badge">
            {meta.label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
          <div>
            <span className="text-muted-foreground">Requested ID</span>
            <p className="font-mono font-semibold" data-testid="sender-id-value">
              {request.requested_sender_id}
            </p>
          </div>
          <div>
            <span className="text-muted-foreground">Submitted</span>
            <p>{new Date(request.created_at).toLocaleDateString()}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Contact</span>
            <p>
              {request.contact_first_name} {request.contact_last_name} —{' '}
              {request.contact_email}
            </p>
          </div>
          <div>
            <span className="text-muted-foreground">Organisation type</span>
            <p>
              {ORG_TYPE_OPTIONS.find((o) => o.value === request.organisation_type)?.label ||
                request.organisation_type}
            </p>
          </div>
        </div>
        {request.admin_notes && (
          <div className="rounded-md border bg-muted/40 p-3">
            <span className="text-muted-foreground text-xs">Admin notes</span>
            <p className="mt-1">{request.admin_notes}</p>
          </div>
        )}
        {request.status === 'rejected' && (
          <p className="text-xs text-muted-foreground">
            You can submit a new request below — please correct the issue raised in the admin notes first.
          </p>
        )}
        {request.status === 'active' && (
          <p className="text-emerald-700 text-xs">
            All SMS sent from your studio now go out from{' '}
            <span className="font-semibold">{request.requested_sender_id}</span> instead of the platform default.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

// ───────────────────────── Form card ─────────────────────────
const FormCard = ({ form, setForm, copyAddress, setCopyAddress, submitting, onSubmit, isResubmit }) => {
  const update = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((p) => ({ ...p, [k]: v }));
  };
  return (
    <Card data-testid="sender-id-form-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="w-5 h-5" />
          {isResubmit ? 'Submit a New Request' : 'Custom Sender ID'}
        </CardTitle>
        <CardDescription>
          Replace the default platform sender ID with your own personalised 11-character alpha tag.
          We'll submit your details to ComReg for approval. €49 recurring annual fee.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-6">
          {/* Sender ID */}
          <div>
            <Label htmlFor="sid">Requested Sender ID *</Label>
            <Input
              id="sid"
              data-testid="sender-id-input"
              value={form.requested_sender_id}
              onChange={(e) => {
                const v = e.target.value.replace(/[^A-Za-z0-9]/g, '').slice(0, 11);
                setForm((p) => ({ ...p, requested_sender_id: v }));
              }}
              maxLength={11}
              placeholder="YogaWithEva"
              required
            />
            <p className="text-xs text-muted-foreground mt-1">
              Maximum 11 alphanumeric characters. No spaces or symbols. {form.requested_sender_id.length}/11
            </p>
          </div>

          {/* Organisation block */}
          <div className="space-y-4">
            <h4 className="font-semibold">Organisation</h4>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Organisation type *</Label>
                <Select value={form.organisation_type} onValueChange={update('organisation_type')}>
                  <SelectTrigger data-testid="org-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ORG_TYPE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Organisation phone *</Label>
                <Input value={form.organisation_phone} onChange={update('organisation_phone')} required />
              </div>
              <div>
                <Label>Organisation email *</Label>
                <Input type="email" value={form.organisation_email} onChange={update('organisation_email')} required />
              </div>
              <div>
                <Label>Tax Registration Number *</Label>
                <Input value={form.tax_registration_number} onChange={update('tax_registration_number')} required />
              </div>
              <div>
                <Label>CRO Number *</Label>
                <Input value={form.cro_number} onChange={update('cro_number')} required />
              </div>
            </div>
          </div>

          {/* Registered address */}
          <div className="space-y-4">
            <h4 className="font-semibold">Registered Address</h4>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label>Address *</Label>
                <Input value={form.registered_address} onChange={update('registered_address')} required />
              </div>
              <div>
                <Label>City / Town *</Label>
                <Input value={form.registered_city} onChange={update('registered_city')} required />
              </div>
              <div>
                <Label>County *</Label>
                <Input value={form.registered_county} onChange={update('registered_county')} required />
              </div>
              <div>
                <Label>EirCode *</Label>
                <Input value={form.registered_eircode} onChange={update('registered_eircode')} required />
              </div>
            </div>
          </div>

          {/* Business address */}
          <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h4 className="font-semibold">Business Address</h4>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={copyAddress}
                  onCheckedChange={(v) => setCopyAddress(!!v)}
                  data-testid="copy-address-checkbox"
                />
                Same as registered address
              </label>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label>Address *</Label>
                <Input
                  value={form.business_address}
                  onChange={update('business_address')}
                  disabled={copyAddress}
                  required
                />
              </div>
              <div>
                <Label>City / Town *</Label>
                <Input
                  value={form.business_city}
                  onChange={update('business_city')}
                  disabled={copyAddress}
                  required
                />
              </div>
              <div>
                <Label>County *</Label>
                <Input
                  value={form.business_county}
                  onChange={update('business_county')}
                  disabled={copyAddress}
                  required
                />
              </div>
              <div>
                <Label>EirCode *</Label>
                <Input
                  value={form.business_eircode}
                  onChange={update('business_eircode')}
                  disabled={copyAddress}
                  required
                />
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="space-y-4">
            <h4 className="font-semibold">Primary Contact</h4>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>First name *</Label>
                <Input value={form.contact_first_name} onChange={update('contact_first_name')} required />
              </div>
              <div>
                <Label>Last name *</Label>
                <Input value={form.contact_last_name} onChange={update('contact_last_name')} required />
              </div>
              <div>
                <Label>Phone *</Label>
                <Input value={form.contact_phone} onChange={update('contact_phone')} required />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" value={form.contact_email} onChange={update('contact_email')} required />
              </div>
            </div>
          </div>

          {/* Fee acknowledgement */}
          <label className="flex items-start gap-3 rounded-lg border p-4 bg-amber-50 cursor-pointer">
            <Checkbox
              checked={form.fee_acknowledged}
              onCheckedChange={(v) => setForm((p) => ({ ...p, fee_acknowledged: !!v }))}
              className="mt-0.5"
              data-testid="fee-ack-checkbox"
            />
            <span className="text-sm">
              I acknowledge that adding a personalised Sender ID is an add-on to my current plan and will be billed at a annual cost of €49.
            </span>
          </label>

          <div className="flex justify-end">
            <Button type="submit" disabled={submitting} data-testid="submit-sender-id-btn">
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Submit Request
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default CustomSenderIdSection;
